import React, { useState, useEffect } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as yup from 'yup';
import { Container, Row, Col, Card, Button, BootstrapForm, Spinner, Alert, Badge } from 'react-bootstrap';
import './tokenlyglobal.css';
import './tokenly.css';

const validationSchema = yup.object().shape({
    fullname: yup.string().required('Doctor name is required'),
    phone: yup.string().required('Mobile number is required').matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits'),
});

const MyDoctors = () => {
    const [isLoading, setLoading] = useState(true);
    const [dataSource, setDataSource] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [staffVal, setStaffVal] = useState({
        id: null,
        fullname: '',
        phone: '',
        can_next_skip: true,
        can_print_token: true,
        can_open_token: true,
        role_id: 3,
    });

    const token = localStorage.getItem("tokenly_token");

    useEffect(() => {
        getData();
    }, []);

    const handleApiResponse = (flag, data, isError, msg) => {
        if (isError) {
            setError(msg || "Something went wrong");
            setTimeout(() => setError(''), 4000);
        } else {
            switch (flag) {
                case 'getData':
                    setDataSource(data || []);
                    break;
                case 'editStaff':
                    setStaffVal({
                        id: data.id,
                        fullname: data.fullname,
                        phone: data.phone,
                        can_next_skip: data.can_next_skip == 1,
                        can_print_token: data.can_print_token == 1,
                        can_open_token: data.can_open_token == 1,
                        role_id: 3,
                    });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    break;
                case 'staffAdd':
                    setStaffVal({
                        id: null,
                        fullname: '',
                        phone: '',
                        can_next_skip: true,
                        can_print_token: true,
                        can_open_token: true,
                        role_id: 3,
                    });
                    getData();
                    setSuccess(msg);
                    setTimeout(() => setSuccess(''), 4000);
                    break;
                case 'staffRemove':
                    getData();
                    setSuccess(msg);
                    setTimeout(() => setSuccess(''), 4000);
                    break;
                default:
                    break;
            }
        }
        setLoading(false);
    };

    const getData = async () => {
        setLoading(true);
        try {
            const res = await fetch("https://tokenly.biswaas.com/api/user/staffList", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ role_id: 3 })
            });
            const json = await res.json();
            handleApiResponse('getData', json.data, !json.status, json.message);
        } catch (err) {
            handleApiResponse('getData', null, true, "Failed to connect to server");
        }
    };

    const removeStaff = async (itemId) => {
        if (!window.confirm("Are you sure you want to remove this doctor?")) return;
        setLoading(true);
        try {
            const res = await fetch("https://tokenly.biswaas.com/api/user/staffRemove", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ itemId })
            });
            const json = await res.json();
            handleApiResponse('staffRemove', json.data, !json.status, json.message);
        } catch (err) {
            handleApiResponse('staffRemove', null, true, "Failed to remove staff");
        }
    };

    const editStaff = async (itemId) => {
        setLoading(true);
        try {
            const res = await fetch("https://tokenly.biswaas.com/api/doctor-detail", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ itemId })
            });
            const json = await res.json();
            handleApiResponse('editStaff', json.data, !json.status, json.message);
        } catch (err) {
            handleApiResponse('editStaff', null, true, "Failed to fetch details");
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const res = await fetch("https://tokenly.biswaas.com/api/user/staffAdd", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ ...values, role_id: 3 })
            });
            const json = await res.json();
            handleApiResponse('staffAdd', json.data, !json.status, json.message);
        } catch (err) {
            handleApiResponse('staffAdd', null, true, "Failed to save data");
        }
    };

    return (
        <div className="bg-light min-vh-100 py-5">
            <Container style={{ maxWidth: '900px' }}>
                <div className="text-center mb-5">
                    <h2 className="fw-bold text-dark">
                        My <span className="text-primary">Doctors</span>
                    </h2>
                    <p className="text-muted">Manage your clinic doctors and their permissions</p>
                </div>

                {error && <Alert variant="danger" className="rounded-3 shadow-sm">{error}</Alert>}
                {success && <Alert variant="success" className="rounded-3 shadow-sm">{success}</Alert>}

                <Card className="border-0 shadow-sm rounded-4 mb-4">
                    <Card.Header className="bg-primary text-white py-3 border-0 rounded-top-4">
                        <h5 className="mb-0 fw-bold">
                            {staffVal.id ? <><i className="bi bi-pencil-square me-2"></i>Edit Doctor</> : <><i className="bi bi-person-plus-fill me-2"></i>Add New Doctor</>}
                        </h5>
                    </Card.Header>
                    <Card.Body className="p-4">
                        <Formik
                            enableReinitialize={true}
                            initialValues={staffVal}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ handleChange, values, errors, touched, setFieldValue }) => (
                                <Form>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <div className="form-group mb-3">
                                                <label className="form-label fw-bold small text-muted">DOCTOR NAME *</label>
                                                <Field
                                                    name="fullname"
                                                    className={`form-control py-2 rounded-3 ${touched.fullname && errors.fullname ? 'is-invalid' : ''}`}
                                                    placeholder="Enter full name"
                                                />
                                                <ErrorMessage name="fullname" component="div" className="invalid-feedback" />
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="form-group mb-3">
                                                <label className="form-label fw-bold small text-muted">MOBILE NO *</label>
                                                <Field
                                                    name="phone"
                                                    className={`form-control py-2 rounded-3 ${touched.phone && errors.phone ? 'is-invalid' : ''}`}
                                                    placeholder="Enter 10 digit mobile"
                                                    maxLength="10"
                                                />
                                                <ErrorMessage name="phone" component="div" className="invalid-feedback" />
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className="p-3 bg-light rounded-3 mb-4 mt-2">
                                        <label className="form-label fw-bold small text-muted mb-3 d-block">PERMISSIONS</label>
                                        <div className="d-flex flex-wrap gap-4">
                                            {[
                                                { id: 'can_open_token', label: 'Start Treatment', icon: 'bi-play-circle' },
                                                { id: 'can_next_skip', label: 'Call Next/Skip', icon: 'bi-skip-forward' },
                                                { id: 'can_print_token', label: 'Generate Token', icon: 'bi-printer' }
                                            ].map(p => (
                                                <div key={p.id} className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input shadow-none"
                                                        id={p.id}
                                                        checked={values[p.id]}
                                                        onChange={(e) => setFieldValue(p.id, e.target.checked)}
                                                    />
                                                    <label className="form-check-label small" htmlFor={p.id}>
                                                        <i className={`bi ${p.icon} me-1 text-primary`}></i> {p.label}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2 justify-content-center">
                                        {staffVal.id && (
                                            <Button
                                                variant="outline-secondary"
                                                className="px-4 py-2 rounded-3 fw-bold"
                                                onClick={() => setStaffVal({ id: null, fullname: '', phone: '', can_next_skip: true, can_print_token: true, can_open_token: true, role_id: 3 })}
                                            >
                                                CANCEL
                                            </Button>
                                        )}
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="px-5 py-2 rounded-3 fw-bold shadow-sm"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? <Spinner size="sm" className="me-2" /> : null}
                                            {staffVal.id ? 'UPDATE DOCTOR' : 'SAVE DOCTOR'}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Card.Body>
                </Card>

                <h5 className="fw-bold text-dark mt-5 mb-3 d-flex align-items-center">
                    <i className="bi bi-people-fill me-2 text-primary"></i>
                    Doctor List <Badge bg="light" text="dark" className="ms-2 border small">{dataSource.length}</Badge>
                </h5>

                <div className="row g-3">
                    {dataSource.length === 0 ? (
                        <div className="col-12 py-5 text-center text-muted">
                            <i className="bi bi-person-exclamation display-4 d-block mb-3"></i>
                            <p>No doctors found in your list.</p>
                        </div>
                    ) : (
                        dataSource.map((item) => (
                            <div key={item.id} className="col-12">
                                <Card className="border-0 shadow-sm rounded-4 overflow-hidden staff-card">
                                    <Card.Body className="p-3">
                                        <Row className="align-items-center">
                                            <Col xs="auto">
                                                {item.profile_photo_full ? (
                                                    <img
                                                        src={item.profile_photo_full}
                                                        alt={item.fullname}
                                                        className="rounded-circle border border-2 border-white shadow-sm"
                                                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold fs-4 shadow-sm" style={{ width: '60px', height: '60px' }}>
                                                        {item.fullname[0]}
                                                    </div>
                                                )}
                                            </Col>
                                            <Col>
                                                <h6 className="mb-0 fw-bold">{item.fullname}</h6>
                                                <p className="small text-muted mb-1"><i className="bi bi-telephone me-1"></i> {item.phone}</p>
                                                <div className="d-flex flex-wrap gap-1">
                                                    {item.can_open_token == 1 && <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 py-1 px-2 fw-normal" style={{ fontSize: '0.65rem' }}>Start Treatment</span>}
                                                    {item.can_next_skip == 1 && <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 py-1 px-2 fw-normal" style={{ fontSize: '0.65rem' }}>Call Next</span>}
                                                </div>
                                            </Col>
                                            <Col xs="auto">
                                                <div className="d-flex gap-2">
                                                    <Button variant="light" className="rounded-3 shadow-sm border" size="sm" onClick={() => editStaff(item.id)}>
                                                        <i className="bi bi-pencil-fill text-primary"></i>
                                                    </Button>
                                                    <Button variant="light" className="rounded-3 shadow-sm border" size="sm" onClick={() => removeStaff(item.id)}>
                                                        <i className="bi bi-trash-fill text-danger"></i>
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </div>
                        ))
                    )}
                </div>
            </Container>
        </div>
    );
};

export default MyDoctors;
